/// <amd-module name="Build/Shared/Components/Timeline/Timeline" />
import * as React from "react";

import {
    IContributedTimelineSnapshotProps,
    ILazySnapshotProps,
    IStatusIconProps,
    ITimelineProps,
    ITimelineSnapshotProps,
    ITimelineSnapshotWrapperProps
} from "./Timeline.types";

import { css } from "VSSUI/Util";

import {
    Components,
    IVssLayoutManager,
    VssComponent,
    WrappedComponent
} from "VSS/Platform/Layout";

import { Icon } from "VSSUI/Icon";
import { Status, StatusSize } from "VSSUI/Status";

type TimelineSnapshotGenericType<T> = new (props: ITimelineSnapshotProps<T>) => TimelineSnapshot<T>;
type TimelineSnapshotReactElement<T> = React.ReactElement<ITimelineSnapshotProps<T>>;
type TimelineLazySnapshotReactElement = React.ReactElement<ILazySnapshotProps>;

export interface ITimelineState<T> {
    orderedItemKeys: string[];
    contributedItemsAvailable: boolean;
}

/**
 * Timeline wrapper for snapshots. This is bound to resource, which is sent to snapshots.
 * Order: Displays latest snapshots on top, latest is determined based on the "marker" Date stamp that each snapshot initializes
 * Every snapshot needs to have it's own unique key and has to initialize with it's prefered "marker" for it to show up
 * Snapshot can be of type React.Component<ITimelineSnapshotProps, {}>
 *   TimelineSnapshot can be directly used if this file is imported, else, any component will work as long as it has those props.
 */
export class Timeline<T> extends VssComponent<ITimelineProps<T>, ITimelineState<T>> {
    // This tells which snapshot is initialized, we need to snapshot to call us back and tell the "marker" based on resource
    //  "marker" determines the sort order of snapshots
    private _itemKeyToInitializedState: { [key: string]: boolean } = {};

    // These store markers, keys for easy look ups, so that we can determine the sort order
    //  note that consumers can send the same marker based on the resource, currently the order for snapshots with in the same marker is not deterministic    
    private _itemKeyToMarker: { [key: string]: Date } = {};
    private _markerToItemKeys: { [key: string]: string[] } = {};

    // This stores refs of contributed elements, this is needed since we allow the "snapshot" element to be anymore
    //  and we use WrappedComponent to fetch the contribution, once the snapshot is available, we fetch the ref
    //  and store it to render
    private _contributedItemKeyToElement: { [key: string]: JSX.Element } = {};
    private _lazyItemKeyToElement: { [key: string]: JSX.Element } = {};

    private _mounted = false;
    private _itemsPendingInitialization: { [key: string]: TimelineSnapshotReactElement<T> } = {};

    private _lazyItems: TimelineLazySnapshotReactElement[] = [];

    private _contributedItems: Set<string> = new Set();
    private _currentContributedItems: Set<string> = new Set();

    constructor(props: ITimelineProps<T>) {
        super(props);
        this.state = {
            orderedItemKeys: [],
            contributedItemsAvailable: props.showAllContributedItemsAtOnce ? false : true
        };
    }

    public render() {
        return <div className={`build-shared-timeline flex ${this.props.className || ""}`}>
            <div className="contributed-snapshot-mounter hidden">
                {
                    /* This will be hidden from the view, this is used to mount the contributed snapshots
                     *  this would allow us to initialize the snapshots and get the ref for the snapshot
                     *  so that we can use it later based on the sort order.
                     * We would need to do something like that because we can't just display the WrappedComponents
                     *  we would need access to the actual props sent to the component (like icontype, initializeSnapshot method)
                     *  we would also need to determine the sort order based on marker sent by the contributed one
                     */
                }
                {this._getContributedChildren()}
                {this._getLazyItems()}
            </div>
            {this._getItemsInOrder(this.state.orderedItemKeys)}
        </div>;
    }

    public componentDidMount() {
        super.componentDidMount();
        this._mounted = true;

        this._initializeItems(this.props.resource, this.props.children);
        // after wait time, let's go ahead and show what we have
        this.setTimeout(() => {
            if (!this.state.contributedItemsAvailable) {
                this.setState({
                    contributedItemsAvailable: true
                });
            }
        }, 1000);
    }

    public UNSAFE_componentWillReceiveProps(nextProps: ITimelineProps<T>) {
        let shouldInitializeItems = true;
        if (this.props.resource === nextProps.resource && React.Children.count(this.props.children) === React.Children.count(nextProps.children)) {
            shouldInitializeItems = false;
            // Note: Items are same, but keys might have changes
            // This can happen for example - sending null based on some props, and now sending element based on new props
            // or changing the order based on new props
            // Children count is same, but elements are changed
            const originalChildren = React.Children.toArray(this.props.children);
            const newChildren = React.Children.toArray(nextProps.children);
            for (let index = 0; index < originalChildren.length; index++) {
                const originalElement = originalChildren[index] as TimelineSnapshotReactElement<T>;
                if (originalElement && originalElement.props) {
                    const newElement = newChildren[index] as TimelineSnapshotReactElement<T>;
                    if (newElement && newElement.props) {
                        // corresponding element is found!
                        if (originalElement.props.snapshotKey !== newElement.props.snapshotKey) {
                            shouldInitializeItems = true;
                            break;
                        }
                    }
                    else {
                        // corresponding element is not found!
                        shouldInitializeItems = true;
                        break;
                    }
                }
            }
        }

        if (shouldInitializeItems) {
            this._clearInitialization();
            this._initializeItems(nextProps.resource, nextProps.children);
        }

        Object.keys(this._itemsPendingInitialization).forEach((key) => {
            this._initializeItem(nextProps.resource, this._itemsPendingInitialization[key]);
        });
    }

    public componentWillUnmount() {
        super.componentWillUnmount();
        this._mounted = false;
        this._clearInitialization();
    }

    private _getItemsInOrder(orderedItemKeys: string[]): JSX.Element[] {
        const elements: JSX.Element[] = [];
        const itemKeyToElement: { [key: string]: JSX.Element } = {};
        const snapshotElements: TimelineSnapshotReactElement<T>[] = [];

        // Get all children sent through props
        React.Children.map(this.props.children, (child, index) => {
            const childComponent = child as TimelineSnapshotReactElement<T>;
            snapshotElements.push(childComponent);
        });

        // Add all lazy elements
        Object.keys(this._lazyItemKeyToElement).forEach((key) => {
            snapshotElements.push(this._lazyItemKeyToElement[key]);
        });

        // Add all contributed elements
        if (this.state.contributedItemsAvailable) {
            Object.keys(this._contributedItemKeyToElement).forEach((key) => {
                snapshotElements.push(this._contributedItemKeyToElement[key]);
            });
        }

        // Convert all elements to the prefered layout
        snapshotElements.forEach((snapshotElement, index) => {
            const childProps = snapshotElement && snapshotElement.props;
            const key = childProps && childProps.snapshotKey;
            if (key) {
                const TimelineSnapshotGeneric = TimelineSnapshot as TimelineSnapshotGenericType<T>;
                const element = <TimelineSnapshotGeneric
                    key={snapshotElement.props.snapshotKey}
                    {...snapshotElement.props}>
                    {snapshotElement.props.children}
                </TimelineSnapshotGeneric>;
                itemKeyToElement[key] = element;
            }
        });

        // Grab elements in order
        orderedItemKeys.forEach((key) => {
            const element = itemKeyToElement[key];
            if (element) {
                elements.push(element);
            }
        });

        return elements;
    }

    private _getContributedChildren(): React.ReactNode[] {
        const nodes: React.ReactNode[] = [];
        if (this.props.regionForSnapshots) {
            const layoutManager = this.context.pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
            const contributedSnapshots = layoutManager.getComponentsForRegion(this.props.regionForSnapshots);
            contributedSnapshots.value.map((childComponent, childIndex) => {
                const props = childComponent.componentProperties as ITimelineSnapshotWrapperProps;
                const extraProps = {
                    snapshotRef: this._initializeContributedItem,
                    projectId: this.props.projectId
                } as IContributedTimelineSnapshotProps;
                if (props.snapshotKey) {
                    this._contributedItems.add(props.snapshotKey);
                    nodes.push(<WrappedComponent
                        key={childIndex}
                        wrappedType={childComponent.componentType}
                        {...props}
                        {...extraProps}
                    />);
                }
            });
        }

        return nodes;
    }

    private _getLazyItems(): React.ReactNode[] {
        const nodes: React.ReactNode[] = [];
        this._lazyItems.forEach((holder) => {
            const extraProps = {
                ...holder.props,
                snapshotRef: this._initializeLazyItem,
            } as ILazySnapshotProps;
            nodes.push(React.cloneElement(holder, extraProps));
        });
        return nodes;
    }

    private _initializeContributedItem = (ref: TimelineSnapshotReactElement<T>) => {
        this._initializeLazyItem(ref, true);
    }

    private _initializeLazyItem = (ref: TimelineSnapshotReactElement<T>, isContributed?: boolean) => {
        const childProps = ref && ref.props;
        const key = childProps && childProps.snapshotKey;
        if (key) {
            if (isContributed) {
                this._contributedItemKeyToElement[key] = ref;
                this._currentContributedItems.add(key);
                if (this._currentContributedItems.size === this._contributedItems.size) {
                    // all are registered, show all
                    this.setState({
                        contributedItemsAvailable: true
                    });
                }
            }
            else {
                this._lazyItemKeyToElement[key] = ref;
            }
            this._initializeItem(this.props.resource, ref);
        }
    }

    private _initializeItems(resource: T, children: React.ReactNode) {
        React.Children.map(children, (element, index) => {
            const childComponent = element as TimelineSnapshotReactElement<T>;
            this._initializeItem(resource, childComponent);
        });

        Object.keys(this._contributedItemKeyToElement).forEach((key) => {
            this._initializeItem(resource, this._contributedItemKeyToElement[key]);
        });

        Object.keys(this._lazyItemKeyToElement).forEach((key) => {
            this._initializeItem(resource, this._lazyItemKeyToElement[key]);
        });
    }

    private _initializeItem(resource: T, element: TimelineSnapshotReactElement<T>) {
        const childProps = element && element.props;
        const key = childProps && childProps.snapshotKey;
        if (key) {
            if (!this._itemKeyToInitializedState[key] && childProps.initializeSnapshot) {
                // Element is not recorded yet, so initialize it here
                console.info("Timeline Shared Component: Initializing component " + key);
                childProps.initializeSnapshot(resource, (marker) => {
                    if (!!marker && marker instanceof Date) {
                        this._itemKeyToMarker[key] = marker;
                        const markerKey = marker.toString();
                        this._markerToItemKeys[markerKey] = this._markerToItemKeys[markerKey] || [];
                        this._markerToItemKeys[markerKey].push(key);
                        this._updateItemKeys();
                        this._itemKeyToInitializedState[key] = true;
                        if (this._itemsPendingInitialization[key]) {
                            delete this._itemsPendingInitialization[key];
                        }

                        console.info("Timeline Shared Component: Initialized component " + key + " with marker", marker);
                    }
                    else {
                        // sometimes for the resource, the marker might not be set yet, allow them to be initialized later on when we get new resource
                        this._itemsPendingInitialization[key] = element;
                        console.warn("Timeline Shared Component: Marker is not of type Date from component with snapshot key: " + key + "...this won't show up");
                    }
                }, () => {
                    this.forceUpdate();
                });
            }
        }
        else {
            const props = childProps as any as ILazySnapshotProps;
            // If we have snapshotRef defined, let's consider this as lazy, snapshotRef is injected by us later, so don't worry about being valid ref
            if (props && props.snapshotRef) {
                this._lazyItems.push(element as any);
            }
            else {
                console.warn(`Timeline Shared Component: snapshotKey is not given for one of the child, that element won't render.`);
            }
        }
    }

    private _updateItemKeys() {
        // Get all existing markers and sort them by desc date
        const sortedMarkers = Object.keys(this._markerToItemKeys).sort((a, b) => {
            const dateB = new Date(b).getTime();
            const dateA = new Date(a).getTime();
            return dateB - dateA;
        });

        // Grab items for those markers in order and ask component to render now
        let orderedItemKeys: string[] = [];
        sortedMarkers.forEach(marker => {
            orderedItemKeys = orderedItemKeys.concat(this._markerToItemKeys[marker]);
        });

        this._setState(orderedItemKeys);
    }

    private _setState(orderedItemKeys: string[]) {
        if (this._mounted) {
            const uniqueKeys: string[] = orderedItemKeys.filter((value, index, array) => {
                return array.indexOf(value) === index;
            });
            this.setState({
                orderedItemKeys: uniqueKeys
            });
        }
    }

    private _clearInitialization() {
        this._itemKeyToInitializedState = {};
    }
}

export class TimelineSnapshot<T> extends React.Component<ITimelineSnapshotProps<T>, {}> {
    public render() {
        const statusCounter = this.props.statusIconProps as number;
        const counter = !isNaN(statusCounter) ? statusCounter : undefined;
        const statusIconProps = this.props.statusIconProps as IStatusIconProps;
        const isCounter = counter !== undefined;
        const hasStatusIconProps = !isCounter && statusIconProps;
        return <div className={css("snapshot", this.props.className)}>
            <div className="marker-col flex flex-grow flex-center">
                <div className="marker-icon relative flex flex-center justify-center">
                    {
                        this.props.onRenderIcon ? this.props.onRenderIcon() :
                            this.props.iconProps &&
                            <Icon
                                iconName={this.props.iconProps.name}
                                className={css("content", this.props.iconProps.className)}
                            />
                    }
                    {
                        isCounter && (
                            <span className="marker-status-icon absolute">
                                <span className="counter-status fontWeightHeavy">
                                    <span className="counter-value">{counter}</span>
                                </span>
                            </span>
                        )
                    }
                    {
                        hasStatusIconProps && statusIconProps.iconName && (
                            <span className="marker-status-icon absolute">
                                <Icon
                                    iconName={statusIconProps.iconName}
                                    className={statusIconProps.className}
                                />
                            </span>
                        )
                    }
                    {
                        hasStatusIconProps && statusIconProps.statusProps && (
                            <span className="marker-status-icon absolute">
                                <Status {...statusIconProps.statusProps} size={StatusSize.l} />
                            </span>
                        )
                    }
                </div>
                <div className="beam">
                </div>
            </div>
            <div className="content-col">
                {this.props.children}
            </div>
        </div>;
    }
}

Components.add("shared-timeline", Timeline);
